import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import { path as rPath } from "ramda";

import useLogger from "@/composables/useLogger";

const logger = useLogger("router");

const ReportComponentsIndex = () => import("@/components/reports/Index.vue");

const DashboardIndex = () => import("@/components/dashboard/Index.vue");

const DealerComponentsIndex = () => import("@/components/dealers/Index.vue");
const DealerComponentsNew = () => import("@/components/dealers/New.vue");
const DealerComponentsEdit = () => import("@/components/dealers/Edit.vue");
// const DealerComponentsShow = () => import('@/components/dealers/Show.vue')

const UserComponentsIndex = () => import("@/components/user/Index.vue");
const UserComponentsNew = () => import("@/components/user/New.vue");
const UserComponentsEdit = () => import("@/components/user/Edit.vue");
const UserComponentsLogin = () => import("@/components/user/Login.vue");
const UserComponentsCompleteRegistration = () =>
  import("@/components/user/CompleteRegistration.vue");

const ProfileComponentsEdit = () => import("@/components/profile/Edit.vue");
const ProfileComponentsConfigureMfa = () =>
  import("@/components/profile/ConfigureMfa.vue");
const ProfileComponentsChangePassword = () =>
  import("@/components/profile/ChangePassword.vue");

const CustomerAnonymizationIndex = () =>
  import("@/components/customer_anonymization/Index.vue");
const ProfileComponentsForgotPassword = () =>
  import("@/components/profile/ResetForgottenPassword.vue");

const EmailBuilderComponentsIndex = () =>
  import("@/components/email_builder/Index.vue");
const EmailBuilderComponentsNew = () =>
  import("@/components/email_builder/New.vue");
const EmailBuilderComponentsEdit = () =>
  import("@/components/email_builder/Edit.vue");
const EmailBuilderComponentsSnippetIndex = () =>
  import("@/components/email_builder/snippets/Index.vue");
const EmailBuilderComponentsSnippetNew = () =>
  import("@/components/email_builder/snippets/New.vue");
const EmailBuilderComponentsSnippetEdit = () =>
  import("@/components/email_builder/snippets/Edit.vue");

const CsvDatasourceComponentsIndex = () =>
  import("@/components/datasources/csv/List.vue");
const CsvDatasourceComponentsNew = () =>
  import("@/components/datasources/csv/New.vue");
const CsvDatasourceComponentsShow = () =>
  import("@/components/datasources/csv/Show.vue");

const DmsDatasourceComponentsIndex = () =>
  import("@/components/datasources/dms/List.vue");
const DmsDatasourceComponentsNew = () =>
  import("@/components/datasources/dms/New.vue");
const DmsDatasourceComponentsEdit = () =>
  import("@/components/datasources/dms/Edit.vue");
const DmsDatasourceComponentsTemplateEdit = () =>
  import("@/components/datasources/dms/TemplateEdit.vue");

const DatasourceMailerComponentsIndex = () =>
  import("@/components/datasource_mailers/List.vue");
const DatasourceMailerComponentsNew = () =>
  import("@/components/datasource_mailers/New.vue");
const DatasourceMailerComponentsEdit = () =>
  import("@/components/datasource_mailers/Edit.vue");
const DatasourceMailerComponentsShow = () =>
  import("@/components/datasource_mailers/Show.vue");
const DatasourceMailerComponentsShowAttachment = () =>
  import("@/components/datasource_mailers/ShowAttachment.vue");

// const CsvTableDatsourceComponentsIndex = () => import('@/components/datasources/csvtable/Index.vue')

const SqlDatasourceIndex = () =>
  import("@/components/datasources/sql/List.vue");
const SqlDatasourceEdit = () => import("@/components/datasources/sql/Edit.vue");
const SqlDatasourceNew = () => import("@/components/datasources/sql/New.vue");

const EmailCampaignComponentsIndex = () =>
  import("@/components/campaigns/email/List.vue");
const EmailCampaignComponentsNew = () =>
  import("@/components/campaigns/email/New.vue");
const EmailCampaignComponentsEdit = () =>
  import("@/components/campaigns/email/Edit.vue");
const EmailCampaignComponentsShow = () =>
  import("@/components/campaigns/email/Show.vue");

const SearchComponentsIndex = () =>
  import("@/components/search/OmniSearchIndex.vue");
// const SearchComponentsShow = () => import('@/components/search/OmniSearchShow.vue')

/**
 * CsvDatasourceReports
 */
const CsvDatasourceReportsIndex = () =>
  import("@/components/reports/csv/Index.vue");
const CsvDatasourceReportsNew = () =>
  import("@/components/reports/csv/New.vue");

/**
 * Authenticated Domains
 */
const AuthenticatedDomainIndex = () =>
  import("@/components/authenticated_domains/Index.vue");

Vue.use(Router);

const requireAuth = function (to, from, next) {
  logger.trace("Processing Auth");
  if (to.matched.some((record) => record.meta.noAuth)) {
    next();
  } else {
    store
      .dispatch("getCurrentUser")
      .then(() => next()) // user is authenticated
      .catch((err) => {
        logger.error("Login Required!");
        logger.trace(err);
        next({
          name: "user_login_path",
        });
      });
  }
};

const selectLayout = function (to, from, next) {
  logger.trace("Processing Layout");
  if (to.matched.some((record) => record.meta.fullPageView)) {
    store.commit("ApplicationStore/renderFullPageView");
  } else {
    store.commit("ApplicationStore/renderAppView");
  }
  next();
};

const validateUserConfirmation = function (to, from, next) {
  if (rPath(["user_auth_challenge", "ChallengeName"], store.state.cognito)) {
    next();
  } else {
    next({
      name: "user_login_path",
    });
    Vue.prototype.$flash({
      showClose: true,
      message:
        "The user confirmation is not valid. Please login again to proceed.",
      type: "error",
      duration: 5000,
    });
  }
};

const router = new Router({
  mode: "history",
  routes: [
    { path: "/", component: DashboardIndex, name: "root_path" },
    { path: "/dashboard", component: DashboardIndex, name: "dashboard_path" },
    /* USER - AUTH */
    {
      path: "/user/login",
      component: UserComponentsLogin,
      name: "user_login_path",
      meta: { noAuth: true, fullPageView: true },
    },
    {
      path: "/user/complete_registration",
      component: UserComponentsCompleteRegistration,
      name: "user_complete_registration_path",
      meta: { noAuth: true, fullPageView: true },
      beforeEnter: validateUserConfirmation,
    },
    /**
     * USER CRUD
     */
    { path: "/users", component: UserComponentsIndex, name: "users_path" },
    { path: "/users/new", component: UserComponentsNew, name: "new_user_path" },
    {
      path: "/users/:id/edit",
      component: UserComponentsEdit,
      name: "edit_user_path",
    },
    /**
     * CUSTOMER ANONYMIZATION
     */
    {
      path: "/customer-anonymization",
      component: CustomerAnonymizationIndex,
      name: "customer_anonymization_path",
      meta: { requiresAdmin: true }
    },

    /**
     * Profile Editing...
     */
    {
      path: "/profile/edit",
      component: ProfileComponentsEdit,
      name: "edit_profile_path",
    },
    {
      path: "/profile/configure_mfa",
      component: ProfileComponentsConfigureMfa,
      name: "configure_mfa_profile_path",
    },
    {
      path: "/profile/change_password",
      component: ProfileComponentsChangePassword,
      name: "change_password_profile_path",
    },
    {
      path: "/profile/forgot_password",
      component: ProfileComponentsForgotPassword,
      name: "reset_forgotten_password_path",
      meta: { noAuth: true, fullPageView: true },
    },

    /** CVS Datasource */
    {
      path: "/datasources/csv",
      component: CsvDatasourceComponentsIndex,
      name: "csv_datasources_path",
    },
    {
      path: "/datasources/csv/new",
      component: CsvDatasourceComponentsNew,
      name: "new_csv_datasource_path",
    },
    // { path: '/datasources/csv/edit/:id', component: DatasourceComponents.Edit, name: 'edit_csv_datasource_path' },
    {
      path: "/datasources/csv/:id",
      component: CsvDatasourceComponentsShow,
      name: "show_csv_datasource_path",
    },

    /** DMS Datasource  / QueryBuilder */
    {
      path: "/datasources/dms",
      component: DmsDatasourceComponentsIndex,
      name: "dms_datasources_path",
    },
    {
      path: "/datasources/dms/new",
      component: DmsDatasourceComponentsNew,
      name: "new_dms_datasource_path",
    },
    {
      path: "/datasources/dms/:id/edit",
      component: DmsDatasourceComponentsEdit,
      name: "edit_dms_datasource_path",
    },
    {
      path: "/datasource_template/:id/edit",
      component: DmsDatasourceComponentsTemplateEdit,
      name: "edit_dms_datasource_template_path",
    },

    /** CsvTable Datasource */
    // { path: '/datasources/csvtable', component: CsvTableDatsourceComponentsIndex, name: 'csvtable_datasources_path' },

    /** Sql Datasource */
    {
      path: "/datasources/sql",
      component: SqlDatasourceIndex,
      name: "sql_datasources_path",
    },
    {
      path: "/datasources/sql/new",
      component: SqlDatasourceNew,
      name: "new_sql_datasource_path",
    },
    {
      path: "/datasources/sql/:id/edit",
      component: SqlDatasourceEdit,
      name: "edit_sql_datasource_path",
    },

    /**
     * EMAIL BUILDER
     */
    {
      path: "/email_builder",
      name: "email_builder_path",
      component: EmailBuilderComponentsIndex,
    },
    {
      path: "/email_builder/new",
      name: "new_email_builder_path",
      component: EmailBuilderComponentsNew,
      meta: { fullPageView: true },
    },
    {
      path: "/email_builder/:id/edit",
      name: "edit_email_builder_path",
      component: EmailBuilderComponentsEdit,
      meta: { fullPageView: true },
    },

    /**
     * HTML Snippets
     */
    {
      path: "/email_builder/snippets",
      name: "email_builder_snippets_path",
      component: EmailBuilderComponentsSnippetIndex,
    },
    {
      path: "/email_builder/snippets/new",
      name: "new_email_builder_snippet_path",
      component: EmailBuilderComponentsSnippetNew,
    },
    {
      path: "/email_builder/snippets/:id/edit",
      name: "edit_email_builder_snippet_path",
      component: EmailBuilderComponentsSnippetEdit,
    },
    /**
     * Email Campaigns
     */
    {
      path: "/campaigns/email/new",
      component: EmailCampaignComponentsNew,
      name: "new_email_campaign_path",
    },
    {
      path: "/campaigns/email",
      component: EmailCampaignComponentsIndex,
      name: "email_campaigns_path",
    },
    {
      path: "/campaigns/email/:id/edit",
      component: EmailCampaignComponentsEdit,
      name: "edit_email_campaign_path",
    },
    {
      path: "/campaigns/email/:id",
      component: EmailCampaignComponentsShow,
      name: "email_campaign_path",
    },

    /**
     * Dealers
     */
    {
      path: "/dealers",
      component: DealerComponentsIndex,
      name: "dealers_path",
    },
    {
      path: "/dealers/new",
      component: DealerComponentsNew,
      name: "new_dealer_path",
    },
    {
      path: "/dealers/:id/edit",
      component: DealerComponentsEdit,
      name: "edit_dealer_path",
    },
    // { path: '/dealers/:id', component: DealerComponentsShow, name: 'dealer_path' },

    /**
     * Search
     */
    {
      path: "/search",
      component: SearchComponentsIndex,
      name: "omni_search_path",
    },

    {
      path: "/reports",
      component: ReportComponentsIndex,
      name: "reports_path",
    },

    /**
     * DatasourceMailers
     */
    {
      path: "/datasource_mailers",
      component: DatasourceMailerComponentsIndex,
      name: "datasource_mailers_path",
    },
    {
      path: "/datasource_mailers/new",
      component: DatasourceMailerComponentsNew,
      name: "new_datasource_mailer_path",
    },
    {
      path: "/datasource_mailers/:id/edit",
      component: DatasourceMailerComponentsEdit,
      name: "edit_datasource_mailer_path",
    },
    {
      path: "/datasource_mailers/:id",
      component: DatasourceMailerComponentsShow,
      name: "datasource_mailer_path",
    },
    {
      path: "/datasource_mailers/:datasource_mailer_id/attachments/:id",
      component: DatasourceMailerComponentsShowAttachment,
      name: "datasource_mailer_attachment_path",
    },

    /**
     * CsvDatasource Reports
     */
    {
      path: "/reports/csv",
      component: CsvDatasourceReportsIndex,
      name: "csv_datasource_reports_path",
    },

    {
      path: "/reports/csv/new",
      component: CsvDatasourceReportsNew,
      name: "new_csv_datasource_report_path",
    },

    /**
     * Authenticated Domains
     */

    {
      path: "/authenticated_domains",
      component: AuthenticatedDomainIndex,
      name: "authenticated_domains_path",
    },
  ],
});

router.beforeEach((to, from, next) => {
  return requireAuth(to, from, next);
});

router.beforeResolve((to, from, next) => {
  return selectLayout(to, from, next);
});

export default router;
